import { toast } from 'sonner';
import {
  EFormType,
  EQuestionType,
  isForm,
  isInfo,
  isMultipleSelect,
  isOpen,
  isSingleSelect,
  isVideo,
} from '@hubertai/screening-question-types';
import type {
  TAnyClientQuestionType,
  TQuestionBase,
  TAvailableLanguageCodes,
  TInfoQuestion,
  TSingleSelectQuestion,
  TSingleSelectOption,
  TMultipleSelectOption,
  TMultipleSelectQuestion,
  TOpenQuestion,
  TFormQuestion,
  TFormSubType,
  TCalendarQuestion,
  TRankingQuestion,
  TRecordingQuestion,
  TRankingOption,
  TSchedulingQuestion,
  TQuestionRule,
} from '@hubertai/screening-question-types';
import { ECreateQuestionMode } from './context/create-question.context';
import type { TCreateQuestion } from './context/create-question.context';
import { StripHTML } from '@/utils/utils';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';
import { isHubertAdminUser, isRecruiterUser } from 'types/user.types';
import { DEFAULT_SCHEDULING_CONFIG } from 'types/questions/scheduling.types';
import type { TSchedulingConfig } from 'types/questions/scheduling.types';

const getValue = (
  newValue: { value: { text: string }[] },
  language: TAvailableLanguageCodes,
  existingValue?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>,
) => {
  return existingValue?.[language]?.[0] === newValue.value[0]?.text
    ? existingValue
    : {
        ...existingValue,
        [language]: newValue.value.map((x: { text: string }) => x.text),
      };
};

const withProgressiveMatrices = (forms: TFormSubType[]): TFormSubType[] =>
  forms.map((form) => (form.type === EFormType.Matrix ? { ...form, display_mode: 'progressive' } : form));

export const validateNewQuestion = (
  q: TCreateQuestion,
  language: TAvailableLanguageCodes,
  user: TAdminUser | TRecruiterAppUser,
  mode: ECreateQuestionMode,
  questionConfig?: TAnyClientQuestionType | null,
): (TAnyClientQuestionType & { copiedIdMap?: Record<string, string> }) | null => {
  const isRecruiter = user && isRecruiterUser(user);
  const isAdmin = user && isHubertAdminUser(user);
  const clientId = isRecruiter ? user.client_id : q.client_id;
  const visibility = q.visibility;

  if (!visibility || !visibility.length) {
    toast.error('Please choose a visibility for the question.');
    return null;
  }

  if (isAdmin && visibility === 'client' && (!clientId || !clientId.length)) {
    toast.error('Please choose a client for client-scoped questions.');
    return null;
  }

  if (isRecruiter && ['client', 'local'].includes(visibility ?? '') && (!clientId || !clientId.length)) {
    toast.error('Please choose a client/local for client-scoped questions.');
    return null;
  }

  if (visibility === 'local' && (!q.access_groups_ids || !q.access_groups_ids.length)) {
    toast.error('Please choose an access group for for local-scoped questions.');
    return null;
  }

  const isCopy = mode === ECreateQuestionMode.Copy || mode === ECreateQuestionMode.InTemplateCopy;
  const existingConfig = isCopy ? undefined : questionConfig;

  const question: any = {
    _id: q._id ?? undefined,
    source_question_id: q.source_question_id,
    id: q.id,
    type: q.type,
    short_description: q.short_description,
    version: q.version,
    client_id: clientId,
    access_groups_ids: q.access_groups_ids ?? [],
    visibility: q.visibility,

    pre_actions: q.pre_actions,
    post_actions: q.post_actions,

    tags: questionConfig?.tags ?? [],
    roles: questionConfig?.roles ?? [],
    // q.rules is the local editor's copy (initialized from questionConfig on load, e.g. for a
    // copy, and kept in sync with any rule changes made this session — see upsertQuestionRule).
    // questionConfig.rules is only a fallback for callers that never touch it.
    rules: q.rules ?? questionConfig?.rules ?? [],

    is_reusable: true,
    skip_if_exists: false,
    send_data_to_looker: q.send_data_to_looker ?? false,
    deferrable: q.deferrable,
    available_languages: isCopy ? [language] : questionConfig ? q.availableLanguages : [language],
    presentation: isCopy
      ? { ...q.presentation, label: { [language]: q.presentation.label[language] ?? '' } }
      : q.presentation,

    plain_text: isCopy ? { [language]: q.plain_text[language] ?? '' } : questionConfig ? q.plain_text : {},

    created: { date: new Date(), by: user._id },
    updated: { date: new Date(), by: user._id },
    conversation_settings: q?.conversation_settings ?? undefined,
    copiedIdMap: q.copiedIdMap ?? undefined,
  };

  switch (q.type) {
    case EQuestionType.Info:
      if (isInfo(question)) {
        question.intro = getValue(q.intro, language, existingConfig?.intro);
        question.text = getValue(q.text, language, existingConfig?.text);
        question.outro = getValue(q.outro, language, existingConfig?.outro);
      }
      break;

    case EQuestionType.SingleSelect:
      if (isSingleSelect(question)) {
        question.intro = getValue(q.intro, language, existingConfig?.intro);
        question.text = getValue(q.text, language, existingConfig?.text);
        question.outro = getValue(q.outro, language, existingConfig?.outro);
        // Authored clarification — surfaced only on VOICE when the candidate asks for one (chat is
        // button-click). Mirrors the Open case; the engine reads it via Parser.queryClarify.
        question.clarify = getValue(q.clarify, language, (existingConfig as unknown as TOpenQuestion)?.clarify);
        question.options = isCopy
          ? filterOptions(q.options as TSingleSelectOption[], language)
          : (q.options as TSingleSelectOption[]);
        question.display_mode = q.display_mode;
      }
      break;

    case EQuestionType.MultipleSelect:
      if (isMultipleSelect(question)) {
        question.intro = getValue(q.intro, language, existingConfig?.intro);
        question.text = getValue(q.text, language, existingConfig?.text);
        question.outro = getValue(q.outro, language, existingConfig?.outro);
        question.options = isCopy
          ? filterOptions(q.options as TMultipleSelectOption[], language)
          : (q.options as TMultipleSelectOption[]);
        question.min_selections = q.min_selections;
        question.max_selections = q.max_selections;
        question.help_text = isCopy ? filterLocalizedText(q.help_text, language) : (q.help_text ?? undefined);
      }
      break;
    case EQuestionType.Open:
      if (isOpen(question)) {
        question.intro = getValue(q.intro, language, existingConfig?.intro);
        question.text = getValue(q.text, language, existingConfig?.text);
        question.outro = getValue(q.outro, language, existingConfig?.outro);
        question.clarify = getValue(q.clarify, language, (existingConfig as unknown as TOpenQuestion)?.clarify);
        question.intents = q.intents;
        question.weight = q.weight;
        question.scoring_category = q.scoring_category;
        question.slot_filling = q.slot_filling;
        question.objectives = q.objectives;
      }
      break;
    case EQuestionType.Form:
      if (isForm(question)) {
        question.intro = getValue(q.intro, language, existingConfig?.intro);
        question.text = getValue(q.text, language, existingConfig?.text);
        question.outro = getValue(q.outro, language, existingConfig?.outro);
        question.forms = isCopy ? q.forms : withProgressiveMatrices(q.forms);
      }
      break;
    case EQuestionType.DatePicker:
      question.intro = getValue(q.intro, language, existingConfig?.intro);
      question.text = getValue(q.text, language, existingConfig?.text);
      question.outro = getValue(q.outro, language, existingConfig?.outro);
      break;

    case EQuestionType.MonthPicker:
      break;
    case EQuestionType.Ranking:
      question.intro = getValue(q.intro, language, existingConfig?.intro);
      question.text = getValue(q.text, language, existingConfig?.text);
      question.outro = getValue(q.outro, language, existingConfig?.outro);
      question.options = isCopy
        ? filterOptions(q.options as TRankingOption[], language)
        : (q.options as TRankingOption[]);
      question.help_text = isCopy ? filterLocalizedText(q.help_text, language) : (q.help_text ?? undefined);
      question.randomize_order = q.randomize_order ?? false;
      question.title = isCopy ? filterLocalizedText(q.title, language) : (q.title ?? undefined);
      question.min_required = q.min_required;
      question.mode = q.ranking_mode ?? 'click';
      break;
    case EQuestionType.Audio:
    case EQuestionType.Video:
      question.intro = getValue(q.intro, language, existingConfig?.intro);
      question.text = getValue(q.text, language, existingConfig?.text);
      question.outro = getValue(q.outro, language, existingConfig?.outro);
      question.allow_retake = q.allow_retake ?? true;
      question.required = q.required ?? false;
      if (q.max_duration_seconds !== undefined) question.max_duration_seconds = q.max_duration_seconds;
      if (q.min_duration_seconds !== undefined) question.min_duration_seconds = q.min_duration_seconds;
      if (q.max_retakes !== undefined) question.max_retakes = q.max_retakes;
      if (q.max_thinking_seconds !== undefined) question.max_thinking_seconds = q.max_thinking_seconds;
      if (q.type === EQuestionType.Video && q.recording_quality) question.quality = q.recording_quality;
      break;
    case EQuestionType.Scheduling:
      question.intro = getValue(q.intro, language, existingConfig?.intro);
      question.text = getValue(q.text, language, existingConfig?.text);
      question.outro = getValue(q.outro, language, existingConfig?.outro);
      // Booking-confirmed reply line(s) — edited via SchedulingRepliesPopover; keep the doc's when untouched.
      question.replies = q.replies ?? (existingConfig && 'replies' in existingConfig ? existingConfig.replies : {});
      (question as { scheduling?: TSchedulingConfig }).scheduling = q.scheduling ?? DEFAULT_SCHEDULING_CONFIG;
      break;

    default:
      console.log('No such question type exists!');
      break;
  }
  return question;
};

export const validateUpdatedQuestion = (
  questionConfig: TAnyClientQuestionType,
  q: TCreateQuestion,
  language: TAvailableLanguageCodes,
  user: TAdminUser | TRecruiterAppUser,
  mode: ECreateQuestionMode,
): TAnyClientQuestionType | undefined => {
  const isRecruiter = user && isRecruiterUser(user);
  const isAdmin = user && isHubertAdminUser(user);
  const clientId = isRecruiter ? user.client_id : q.client_id;
  const visibility = q.visibility;

  if (isAdmin && visibility === 'client' && (!clientId || !clientId.length)) {
    toast.error('Please choose a client for client-scoped questions.');
    return;
  }

  if (!questionConfig) {
    toast.error('Update not possible. Wrong question version.');
    return;
  }

  if (!questionConfig.id || !questionConfig.id.length) {
    toast.error('Update not possible. Wrong question version.');
    return;
  }

  if (!questionConfig._id || !questionConfig._id?.length) {
    toast.error('Update not possible. Wrong question version.');
    return;
  }

  if (questionConfig.version !== 4) {
    toast.error('Update not possible. Wrong question version.');
    return;
  }

  const question: TQuestionBase<string> = {
    _id: questionConfig._id,
    id: questionConfig.id,
    short_description: q.short_description,
    version: questionConfig.version,
    client_id: clientId,

    pre_actions: q.pre_actions,
    post_actions: q.post_actions,

    tags: questionConfig.tags,
    roles: questionConfig.roles,
    // q.rules is the local editor's copy, kept in sync with any rule changes made this session
    // (see upsertQuestionRule) — questionConfig.rules is a snapshot from when the page loaded and
    // goes stale the moment a rule is added, so it's only a fallback for callers that never touch it.
    // Typed against the frontend-local TQuestionRule, structurally identical to (but a nominally
    // distinct type from) the shared-package one questionConfig.rules uses.
    rules: (q.rules as TQuestionRule[] | undefined) ?? questionConfig.rules,

    visibility,
    access_groups_ids: q.access_groups_ids ?? [],
    is_reusable: questionConfig.is_reusable,
    skip_if_exists: questionConfig.skip_if_exists,
    send_data_to_looker: q.send_data_to_looker ?? false,
    deferrable: q.deferrable,

    // questionConfig.available_languages alone would leave a just-added `language` (e.g. a
    // reviewed translation on a question that didn't have it yet) unlisted even though
    // text/intro/outro below are being written for it right now — the saved document would look
    // untranslated everywhere that reads available_languages (delivery, the
    // translationPendingReason guard, etc.) despite having the content.
    available_languages: questionConfig.available_languages.includes(language)
      ? questionConfig.available_languages
      : [...questionConfig.available_languages, language],
    presentation: {
      label: {
        ...questionConfig.presentation.label,
        [language]: q.presentation.label[language],
      },
      block: q.presentation.block,
    },

    plain_text: {
      ...questionConfig.plain_text,
      [language]: q.text.value.map((x: { id: string; text: string }) => StripHTML(x.text)).join(' '),
    },

    created: questionConfig.created,
    conversation_settings: q.conversation_settings ?? undefined,
  };

  let updatedQuestion: TAnyClientQuestionType | undefined = undefined;

  function handleIntroTextOutro(
    existing: TAnyClientQuestionType,
    updated: TAnyClientQuestionType,
    edited: TCreateQuestion,
    field: 'intro' | 'text' | 'outro',
  ) {
    updated[field] = { ...existing[field] };
    updated[field][language] = edited[field].value.map((x: { id: string; text: string }) => x.text);
  }

  switch (q.type) {
    case EQuestionType.Info:
      updatedQuestion = question as TInfoQuestion<string>;
      updatedQuestion.type = EQuestionType.Info;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      break;

    case EQuestionType.SingleSelect:
      updatedQuestion = question as TSingleSelectQuestion<string>;
      updatedQuestion.type = EQuestionType.SingleSelect;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');

      updatedQuestion.clarify = { ...(questionConfig as unknown as TOpenQuestion).clarify };
      updatedQuestion.clarify[language] = q.clarify.value.map((x: { id: string; text: string }) => x.text);

      updatedQuestion.options = q.options as TSingleSelectOption[];
      updatedQuestion.display_mode = q.display_mode;
      break;

    case EQuestionType.MultipleSelect:
      updatedQuestion = question as TMultipleSelectQuestion<string>;
      updatedQuestion.help_text = q.help_text ?? undefined;
      updatedQuestion.type = EQuestionType.MultipleSelect;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');

      updatedQuestion.options = [...q.options] as TMultipleSelectOption[];
      updatedQuestion.min_selections = q.min_selections;
      updatedQuestion.max_selections = q.max_selections;
      break;
    case EQuestionType.Open:
      updatedQuestion = question as TOpenQuestion<string>;
      updatedQuestion.type = EQuestionType.Open;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');

      updatedQuestion.clarify = { ...(questionConfig as unknown as TOpenQuestion).clarify };
      updatedQuestion.clarify[language] = q.clarify.value.map((x: { id: string; text: string }) => x.text);

      if ('is_star_question' in questionConfig) {
        updatedQuestion.is_star_question = questionConfig.is_star_question;
      }

      updatedQuestion.intents = [...q.intents];
      updatedQuestion.weight = q.weight;
      updatedQuestion.scoring_category = q.scoring_category;
      updatedQuestion.slot_filling = q.slot_filling;
      updatedQuestion.objectives = q.objectives;
      break;
    case EQuestionType.Form:
      updatedQuestion = question as TFormQuestion<string>;
      updatedQuestion.type = EQuestionType.Form;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      updatedQuestion.forms =
        mode === ECreateQuestionMode.InTemplateEdit ? [...q.forms] : withProgressiveMatrices(q.forms);
      break;

    case EQuestionType.DatePicker:
      updatedQuestion = question as TCalendarQuestion<string>;
      updatedQuestion.type = EQuestionType.DatePicker;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      break;

    case EQuestionType.Ranking:
      updatedQuestion = question as TRankingQuestion<string>;
      updatedQuestion.type = EQuestionType.Ranking;

      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      updatedQuestion.help_text = q.help_text ?? undefined;
      updatedQuestion.title = q.title ?? undefined;
      updatedQuestion.min_required = q.min_required;
      updatedQuestion.mode = q.ranking_mode ?? 'click';
      updatedQuestion.options = [...q.options] as TRankingOption[];
      break;

    case EQuestionType.Audio:
    case EQuestionType.Video:
      updatedQuestion = question as TRecordingQuestion<string>;
      updatedQuestion.type = q.type;
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      updatedQuestion.allow_retake = q.allow_retake ?? true;
      updatedQuestion.required = q.required ?? false;
      updatedQuestion.min_duration_seconds = q.min_duration_seconds;
      updatedQuestion.max_duration_seconds = q.max_duration_seconds;
      updatedQuestion.max_retakes = q.max_retakes;
      updatedQuestion.max_thinking_seconds = q.max_thinking_seconds;
      if (q.type === EQuestionType.Video && isVideo(updatedQuestion)) updatedQuestion.quality = q.recording_quality;
      break;

    case EQuestionType.Scheduling:
      updatedQuestion = question as TSchedulingQuestion<string>;
      updatedQuestion.type = EQuestionType.Scheduling;

      updatedQuestion.scheduling = q.scheduling ?? DEFAULT_SCHEDULING_CONFIG;
      if (!(updatedQuestion.scheduling.event_type_id > 0) || !updatedQuestion.scheduling.cal_link.trim().length) {
        toast.error('Please provide a cal.com event type ID and link (username/event-slug).');
        return;
      }

      updatedQuestion.replies = q.replies ?? ('replies' in questionConfig ? questionConfig.replies : undefined) ?? {};
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'intro');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'text');
      handleIntroTextOutro(questionConfig, updatedQuestion, q, 'outro');
      break;
    case EQuestionType.MonthPicker:
      break;
    default:
      console.log('No such question type exists!');
      break;
  }

  return updatedQuestion;
};

const filterOptions = (
  options: (TSingleSelectOption | TMultipleSelectOption | TRankingOption)[],
  language: TAvailableLanguageCodes,
): (TSingleSelectOption | TMultipleSelectOption | TRankingOption)[] => {
  return options.map((option) => ({
    ...option,
    label: { [language]: option.label[language] ?? '' },
    replies: option.replies ? { [language]: option.replies[language] ?? [] } : undefined,
    enabled: option.enabled ? { [language]: option.enabled[language] ?? true } : undefined,
    actions: option.actions ? { [language]: option.actions[language] ?? [] } : undefined,
  })) as (TSingleSelectOption | TMultipleSelectOption | TRankingOption)[];
};

const filterLocalizedText = (
  text: Partial<Record<TAvailableLanguageCodes, string>> | undefined,
  language: TAvailableLanguageCodes,
) => {
  if (!text) return undefined;
  return { [language]: text[language] ?? '' };
};
